import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Boxes } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { GatePanel } from "../components/modules/GatePanel";
import {
  decidedMessage, gateOf, moduleName, needsPersonText, refusal, runProgress, type Decision, type Refusal, type RunView,
} from "../components/modules/moduleFlow";
import { assetPath } from "../components/register/exports";
import { RunFindings } from "../components/modules/RunFindings";
import { RunState } from "../components/modules/RunsList";
import { NeioTraces } from "../components/neio/TraceList";
import { Empty, ErrorState, Loading } from "../components/States";
import { ApiError, apiGet, apiSend } from "../lib/http";
import { useMe, type Me } from "../lib/me";

/** How often a working run is asked about. */
export const POLL_MS = 3000;

function Header({ run }: { run: RunView }) {
  return (
    <div className="stack" style={{ gap: 6 }}>
      <h2 style={{ fontSize: 26 }}><span className="row"><Boxes size={22} aria-hidden="true" />{moduleName(run.request_type)}</span></h2>
      <p className="row muted" style={{ flexWrap: "wrap" }}>
        {run.package && <span>Package <span className="mono">{run.package}</span></span>}
        {run.tag && <span>Tag <span className="mono">{run.tag}</span></span>}
        <RunState run={run} />
      </p>
    </div>
  );
}

function ProgressCard({ run }: { run: RunView }) {
  const p = runProgress(run);
  const counted = p.total !== undefined && p.total > 0;
  if (!p.active && !counted) return null;
  const pct = counted ? Math.round(((p.done ?? 0) / p.total!) * 100) : undefined;
  return (
    <section className="card stack" style={{ gap: 8 }} aria-label="Progress">
      {counted && <p>{p.done} of {p.total} tags</p>}
      {counted && (
        <div className="progress" role="progressbar" aria-label="Tags finished" aria-valuetext={`${p.done} of ${p.total} tags`}
             aria-valuemin={0} aria-valuemax={p.total} aria-valuenow={p.done}>
          <i style={{ width: `${pct}%` }} />
        </div>
      )}
      {p.active && <p className="muted" style={{ fontSize: 13 }}>{p.label}. This page checks again every few seconds.</p>}
    </section>
  );
}

/** The run detail, without the fetching, so each state can be drawn and tested alone. */
export function RunDetailBody({
  id, loading, error, run, me, busy, refused, onDecide, decided = null,
}: {
  id?: string;
  loading: boolean;
  error: unknown;
  run: RunView | undefined;
  me: Me | null | undefined;
  busy: boolean;
  refused: Refusal | null;
  onDecide: (d: Decision) => void;
  /** What the last decision on this page did, said until the run moves on. */
  decided?: string | null;
}) {
  if (loading) return <Loading what={id ? `Loading the run ${id}` : "Loading the run"} />;
  if (error instanceof ApiError && error.status === 404) {
    return <Empty title="No run at this address">It may have been started on another environment. <Link to="/modules">See all runs</Link>.</Empty>;
  }
  if (error instanceof ApiError && error.status === 403) {
    return (
      <div className="state-box" role="status">
        <div><div className="state-title">Outside your packages</div><p className="muted">{error.message}</p></div>
      </div>
    );
  }
  if (error || !run) return <ErrorState error={error ?? "The run could not be read."} />;
  const p = runProgress(run);
  const stopped = ["failed", "budget_exhausted", "interrupted"].includes(run.status);
  return (
    <div className="stack">
      <Header run={run} />
      {/* Said while the run acts on the decision; once it has ended, its state says what happened. */}
      {decided && (p.active || gateOf(run)) && <p className="card" role="status" style={{ fontSize: 14 }}>{decided}</p>}
      <ProgressCard run={run} />
      {stopped && run.error && (
        <div className={`state-box${run.status === "interrupted" ? "" : " fail"}`} role="status">
          <div><div className="state-title">{p.label}</div><p className="muted">{run.error}</p></div>
        </div>
      )}
      {run.status === "awaiting_review" && !gateOf(run) && (
        <div className="state-box" role="status">
          <div>
            <div className="state-title">Needs a person</div>
            <p className="muted">
              {needsPersonText(run) ?? "This run waits for a person to check its result."}{" "}
              It is listed in the <Link to="/review?state=pending">review queue</Link>, where an engineer approves or
              rejects it with a note.
              {run.package && run.tag && (
                <> The values are on the <Link to={assetPath(run.package, run.tag)}>register entry for {run.tag}</Link>.</>
              )}
            </p>
          </div>
        </div>
      )}
      <GatePanel run={run} me={me} busy={busy} refused={refused} onDecide={onDecide} />
      <RunFindings run={run} />
      {(run.trail ?? []).length > 0 && (
        <details className="card">
          <summary style={{ cursor: "pointer" }}>Steps taken ({run.trail!.length})</summary>
          <ol style={{ paddingLeft: 20, marginTop: 8, fontSize: 13 }}>
            {run.trail!.map((s, i) => (
              <li key={i} className="mono">
                {String(s.step)}{" "}
                {Object.entries(s).filter(([k]) => k !== "step").map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : String(v)}`).join(" ")}
              </li>
            ))}
          </ol>
        </details>
      )}
    </div>
  );
}

/** /modules/runs/:id. Polls while the run works, and takes the decision at its gate. */
export function ModuleRun() {
  const { id = "" } = useParams();
  const { me } = useMe();
  const client = useQueryClient();
  const [refused, setRefused] = useState<Refusal | null>(null);
  const [decided, setDecided] = useState<string | null>(null);
  const key = ["modules", "run", id];
  const query = useQuery({
    queryKey: key,
    queryFn: () => apiGet("/api/v1/modules/runs/{run_id}", { path: { run_id: id } }),
    refetchInterval: (q) => (q.state.data && runProgress(q.state.data).active ? POLL_MS : false),
  });
  const decide = useMutation({
    mutationFn: (decision: Decision) =>
      apiSend("/api/v1/modules/runs/{run_id}/decision", decision, { path: { run_id: id } }),
    onMutate: () => { setRefused(null); setDecided(null); },
    onSuccess: (run, decision) => {
      setDecided(decidedMessage(decision));
      client.setQueryData(key, run);
      void client.invalidateQueries({ queryKey: key });
      void client.invalidateQueries({ queryKey: ["modules", "runs"] });
    },
    onError: (e) => setRefused(refusal(e, "The decision was not recorded")),
  });
  return (
    <div className="page">
      <Link to="/modules" className="row muted" style={{ marginBottom: 12, fontSize: 14 }}>
        <ArrowLeft size={14} aria-hidden="true" /> Modules
      </Link>
      <RunDetailBody
        id={id}
        loading={query.isPending}
        error={query.error}
        run={query.data}
        me={me}
        busy={decide.isPending}
        refused={refused}
        onDecide={(d) => decide.mutate(d)}
        decided={decided}
      />
      {query.data && <div style={{ marginTop: 16 }}><NeioTraces runId={query.data.id} /></div>}
    </div>
  );
}
